import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGesture } from '@use-gesture/react';

import { useImageTypeMetadataContext } from '../../../../context/ImageTypeMetadataContext';
import { sanitiseId } from '../../../../helpers/sanitiseId';
import { useChunkResolver } from '../../../../hooks/use-chunk-resolver';
import { useOverviewChunksLoader } from '../../../../hooks/use-overview-chunks-loader';
import { useThrottle } from '../../../../hooks/use-throttle';
import { ChunkedImageProperties } from '../../../../types/chunkedImage';
import { LengthRange } from '../../../../types/lengthRange';
import { ImageLaneRowConfiguration } from '../configurations';

import { useHoverLocationColour } from './use-hover-location-colour';

export interface CoreLaneProps {
	id: string;
	multiplier: number;
	width: number;
	scale: number;
	lane: LengthRange;
	overviewChunks?: ChunkedImageProperties[];
	configuration: ImageLaneRowConfiguration;
	height: number;
	position: number;
	visibleLanesRange: LengthRange;
	crosshairDepthM?: number;
}

const ImageLaneRowInternal: React.FC<CoreLaneProps> = ({
	id,
	scale,
	lane,
	configuration,
	height,
	width,
	position,
	crosshairDepthM,
}) => {
	const { loaders } = useImageTypeMetadataContext();

	const imageTypeMetadataLoader = useMemo(() => {
		return loaders[configuration.imageTypeId] ?? { imageTypeMetadata: undefined, isError: false, isLoading: false };
	}, [loaders, configuration.imageTypeId]);

	const { imageTypeMetadata } = imageTypeMetadataLoader;

	const imageHeightInPx = useMemo(
		() => (imageTypeMetadata ? imageTypeMetadata.width * scale : 100),
		[imageTypeMetadata, scale],
	);

	const overviewChunks = useOverviewChunksLoader(configuration.imageTypeId, configuration.isUncropped, lane);
	const overviewRes = overviewChunks[0]?.originalResPxPerM;

	// Returns a list of chunks that can be rendered
	const { chunks, backupChunks } = useChunkResolver(imageTypeMetadata, lane, scale, overviewRes);

	const getChunkCoordinates = useCallback(
		(chunk: ChunkedImageProperties): [number, number] => {
			return [
				(chunk.range.depthFrom - lane.depthFrom) * scale,
				chunk.originalResPxPerM ? scale / chunk.originalResPxPerM : 0,
			];
		},
		[lane.depthFrom, scale],
	);

	const chunksToRender = useMemo(
		() =>
			[...chunks, ...(backupChunks ?? []), ...(overviewChunks ?? [])]
				.filter((c) => c && c.range && c.url)
				.sort((a, b) => {
					const compare = a.originalResPxPerM - b.originalResPxPerM;
					if (!compare) return a.range.depthFrom - b.range.depthFrom;
					return compare;
				}),
		[backupChunks, chunks, overviewChunks],
	);

	const svgRef = useRef<SVGSVGElement>(null);

	const [mousePositionInternal, setMousePositionInternal] = useState<[number, number] | undefined>();

	// Throttle the colour-sample driver so the hover tooltip follows the cursor at a bounded
	// rate (see the matching note in IntervalLaneRow). Debouncing with useStableValue either
	// flickered the tooltip (transient = undefined) or pinned it to the last paused position
	// (transient = prev). 50ms keeps the sample rate cheap while staying responsive.
	const stableMousePosition = useThrottle(mousePositionInternal, 50);

	const handleClickInternal = useCallback(
		([x]: [number, number]) => {
			if (!configuration.onClick) return;
			const calculatedY = lane.depthFrom + x / scale;
			configuration.onClick(calculatedY);
		},
		[configuration, lane.depthFrom, scale],
	);

	// `getScreenCTM()` returns the transform from the lane SVG's local coords to viewport coords —
	// derived purely from the SVG attribute hierarchy, untouched by children's painted geometry.
	// `getBoundingClientRect()` is unreliable here: when chunks are rendered via a nested
	// `<svg viewBox>` (overview path), Chromium includes the inner image's full dimensions in the
	// outer lane's BCR, returning a rect that's hundreds of pixels off in y. CTM gives the true
	// top-left so mouse coords stay within the lane's declared bounds.
	const handleMouseMove = useCallback((event: MouseEvent) => {
		const ctm = svgRef?.current?.getScreenCTM();
		if (!ctm) return;
		const newMousePos: [number, number] = [event.clientX - ctm.e, event.clientY - ctm.f];
		setMousePositionInternal((prev) => {
			if (!prev) return newMousePos;
			const [prevX, prevY] = prev;
			const [newX, newY] = newMousePos;
			if (prevX === newX && prevY === newY) return prev;
			return newMousePos;
		});
	}, []);

	useGesture(
		{
			onMouseMove: (event) => handleMouseMove(event.event),
			onMouseLeave: () => {
				setMousePositionInternal(undefined);
			},
			onClick: (state) => {
				const ctm = svgRef?.current?.getScreenCTM();
				if (!ctm) return;
				handleClickInternal([state.event.clientX - ctm.e, state.event.clientY - ctm.f]);
			},
		},
		{ target: svgRef },
	);

	const hoverColourProps = useHoverLocationColour(
		stableMousePosition,
		chunksToRender,
		lane,
		scale,
		configuration.calculateColourOnHover,
	);

	useEffect(() => {
		if (!configuration.onHover) return;
		if (hoverColourProps?.colour && hoverColourProps?.positionY) {
			configuration.onHover(hoverColourProps.positionY, hoverColourProps.colour);
		} else configuration.onHover(undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hoverColourProps?.colour, hoverColourProps?.positionY]);

	return (
		<svg ref={svgRef} width={width} height={height} viewBox={`0 0 ${width} ${height}`} x={0} y={position}>
			<defs>
				<clipPath id={'clip'}>
					<rect x={0} y={0} width={width} height={height}></rect>
				</clipPath>
			</defs>
			{imageTypeMetadata &&
				chunksToRender.map((c, index) => {
					const chunk = c;
					const [, scaleFactor] = getChunkCoordinates(chunk);
					const transform = `translate(${-imageHeightInPx + (chunk.range.depthFrom - lane.depthFrom) * scale}, 0) rotate(-90 ${imageHeightInPx} 0) scale(${scaleFactor}) `;
					const key = `lane-${lane.depthFrom}-${lane.depthTo}-${chunk.key}`;
					const style: React.CSSProperties = {
						position: 'absolute',
						zIndex: index,
						imageRendering: imageTypeMetadata.renderMode ?? 'auto',
					};
					// Overview chunks share a single decoded blob URL and identify their column
					// region via `sourceCrop`. Use a nested SVG viewBox to clip the source to that
					// region — avoids per-chunk re-encoding while preserving the existing transform.
					if (chunk.sourceCrop && chunk.sourceFullSize) {
						const { x, y, width: cw, height: ch } = chunk.sourceCrop;
						const { width: fullW, height: fullH } = chunk.sourceFullSize;
						return (
							<g key={key} transform={transform} style={style}>
								<svg
									width={cw}
									height={ch}
									viewBox={`${x} ${y} ${cw} ${ch}`}
									preserveAspectRatio="none"
								>
									<image href={chunk.url} width={fullW} height={fullH} preserveAspectRatio="none" />
								</svg>
							</g>
						);
					}
					return <image href={chunk.url} transform={transform} key={key} style={style} />;
				})}

			{configuration.draw
				?.filter((d) => d.yM >= lane.depthFrom && d.yM < lane.depthTo)
				.map((d, index) => {
					const xPx = (d.yM - lane.depthFrom) * scale;
					const yPx = (imageTypeMetadata?.width ?? 0) * scale * d.xPercent;
					const xSize = d.lengthM * scale;
					const ySize = d.widthPercent * (imageTypeMetadata?.width ?? 0) * scale;
					return (
						<rect
							key={`${id}--${sanitiseId(configuration.id)}--draw-${index}`}
							x={xPx}
							y={yPx}
							width={xSize}
							height={ySize}
							fill={d.fill ?? 'none'}
							fillOpacity={d.fillOpacity}
							stroke={d.borderColor}
							strokeWidth={d.borderWidthPx}
							strokeOpacity={d.borderOpacity}
							clipPath="url(#clip)"
							data-length={d.yM}
						></rect>
					);
				})}

			{crosshairDepthM !== undefined && crosshairDepthM >= lane.depthFrom && crosshairDepthM < lane.depthTo && (
				<line
					x1={(crosshairDepthM - lane.depthFrom) * scale}
					y1={0}
					x2={(crosshairDepthM - lane.depthFrom) * scale}
					y2={height}
					stroke="white"
					strokeWidth={1}
					clipPath="url(#clip)"
				/>
			)}
		</svg>
	);
};

export const ImageLaneRow = React.memo(ImageLaneRowInternal);
